import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { serverApiFetch } from '@/services/server-api-client'
import { campaignsApi } from '@/services/birdbrain-api-client'

/**
 * Entity search for the ⌘K command palette (BirdBrain 2.0 M3-C3).
 *
 * Debounces the palette input and fans out to three sources in parallel:
 * - Users:      GET /v1/admin/users?search=<q>&limit=5      (server admin API)
 * - Businesses: GET /v1/admin/businesses?search=<q>&limit=5 (server admin API)
 * - Campaigns:  campaignsApi.getCampaigns() filtered client-side by name or id
 *
 * Errors are swallowed per-source (the palette must never error-toast):
 * a failed source just contributes zero rows.
 */

const DEBOUNCE_MS = 250
const STALE_TIME_MS = 30_000
/** The campaigns list is fetched whole and filtered client-side; cache it longer. */
const CAMPAIGNS_STALE_TIME_MS = 60_000
const MIN_QUERY_LENGTH = 2
const RESULT_LIMIT = 5

// ============================================
// Result row shapes (only the fields the palette renders)
// ============================================

export interface UserSearchResult {
  id: string
  name: string | null
  email: string
  role: string
}

export interface BusinessSearchResult {
  id: string
  name: string
  /** Pulled from the untyped business settings blob when present. */
  website?: string
  memberCount: number
  creativeCount: number
}

export interface CampaignSearchResult {
  id: string
  campaignName: string
  platform: string
  platformDisplay: string
  inferredStatus: string
}

interface EntitySearchGroup<T> {
  data: T[]
  loading: boolean
}

export interface EntitySearchResults {
  /** Debounced query actually in flight (may lag the raw input). */
  query: string
  /** True once the debounced query is long enough to search. */
  active: boolean
  users: EntitySearchGroup<UserSearchResult>
  businesses: EntitySearchGroup<BusinessSearchResult>
  campaigns: EntitySearchGroup<CampaignSearchResult>
}

// ============================================
// Server response shapes (subset of what the endpoints return)
// ============================================

/** GET /v1/admin/users → { users, pagination, timestamp } */
interface AdminUsersResponse {
  users: Array<{
    id: string
    name: string | null
    email: string
    role: string
  }>
}

/** GET /v1/admin/businesses → { data, pagination } */
interface AdminBusinessesResponse {
  data: Array<{
    id: string
    name: string
    settings?: Record<string, unknown> | null
    memberCount: number
    creativeCount: number
  }>
}

// ============================================
// Debounce helper
// ============================================

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}

// ============================================
// Main hook
// ============================================

export function useEntitySearch(rawQuery: string): EntitySearchResults {
  const q = useDebouncedValue(rawQuery.trim(), DEBOUNCE_MS)
  const enabled = q.length >= MIN_QUERY_LENGTH

  const usersQuery = useQuery({
    queryKey: ['palette', 'users', q],
    queryFn: async (): Promise<UserSearchResult[]> => {
      const response = await serverApiFetch<AdminUsersResponse>(
        `/v1/admin/users?search=${encodeURIComponent(q)}&limit=${RESULT_LIMIT}`
      )
      return (response.users ?? []).slice(0, RESULT_LIMIT).map(user => ({
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
      }))
    },
    enabled,
    staleTime: STALE_TIME_MS,
    retry: false,
    // Keep the previous results on screen while the next query fetches
    // (react-query v5 keepPreviousData semantics) so typing doesn't flicker.
    placeholderData: prev => prev,
  })

  const businessesQuery = useQuery({
    queryKey: ['palette', 'businesses', q],
    queryFn: async (): Promise<BusinessSearchResult[]> => {
      const response = await serverApiFetch<AdminBusinessesResponse>(
        `/v1/admin/businesses?search=${encodeURIComponent(q)}&limit=${RESULT_LIMIT}`
      )
      return (response.data ?? []).slice(0, RESULT_LIMIT).map(business => {
        const settings = business.settings
        const website =
          settings && typeof settings.website === 'string' ? settings.website : undefined
        return {
          id: business.id,
          name: business.name,
          website,
          memberCount: business.memberCount ?? 0,
          creativeCount: business.creativeCount ?? 0,
        }
      })
    },
    enabled,
    staleTime: STALE_TIME_MS,
    retry: false,
    // Keep the previous results on screen while the next query fetches
    // (react-query v5 keepPreviousData semantics) so typing doesn't flicker.
    placeholderData: prev => prev,
  })

  // The campaigns endpoint has no text search; fetch the full list ONCE (the
  // key has no q, so typing never refetches it) and filter by name substring
  // client-side via select.
  const campaignsQuery = useQuery({
    queryKey: ['palette', 'campaigns'],
    queryFn: async () => {
      const response = await campaignsApi.getCampaigns()
      return response.campaigns ?? []
    },
    enabled,
    staleTime: CAMPAIGNS_STALE_TIME_MS,
    retry: false,
    select: (campaigns): CampaignSearchResult[] => {
      const needle = q.toLowerCase()
      return campaigns
        .filter(
          campaign =>
            campaign.campaignName.toLowerCase().includes(needle) ||
            campaign.id.toLowerCase().includes(needle)
        )
        .slice(0, RESULT_LIMIT)
        .map(campaign => ({
          id: campaign.id,
          campaignName: campaign.campaignName,
          platform: campaign.platform,
          platformDisplay: campaign.platformDisplay,
          inferredStatus: campaign.inferredStatus,
        }))
    },
  })

  // Errors intentionally collapse to empty groups: the palette shows nothing
  // for a failed source instead of surfacing an error state.
  return {
    query: q,
    active: enabled,
    users: {
      data: usersQuery.data ?? [],
      loading: usersQuery.isLoading,
    },
    businesses: {
      data: businessesQuery.data ?? [],
      loading: businessesQuery.isLoading,
    },
    campaigns: {
      data: campaignsQuery.data ?? [],
      loading: campaignsQuery.isLoading,
    },
  }
}
